import { Component, OnInit, OnDestroy, ViewChild, ElementRef, inject } from "@angular/core";
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";

import { Subscription } from "rxjs";

import { TranslateModule, TranslateService } from "@ngx-translate/core";

import { SnackbarService } from "../../shared/components/snackbar/snackbar.service";
import { SignUpEntryService } from "../services/entry.service";
import { SignUpCreateAccountService } from "../services/create-account.service";
import { SignUpNavigationService, Step } from "../services/navigation.service";
import { checkDomainValidator } from "./domain.validator";
import { SignUpCommonLayoutComponent } from "../signupCommonLayout/signup-common-layout.component";
import { MatError, MatFormField } from "@angular/material/form-field";
import { MatIcon } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatButtonModule } from "@angular/material/button";

@Component({
  selector: "onsip-signup-domain",
  templateUrl: "./signup-domain.component.html",
  styleUrls: ["./signup-domain.component.scss"],
  imports: [
    SignUpCommonLayoutComponent,
    TranslateModule,
    ReactiveFormsModule,
    MatFormField,
    MatError,
    MatIcon,
    MatInputModule,
    MatButtonModule,
    FormsModule
  ]
})
export class SignUpDomainComponent implements OnInit, OnDestroy {
  private translate = inject(TranslateService);
  private createAccountService = inject(SignUpCreateAccountService);
  private entryService = inject(SignUpEntryService);
  private signupNav = inject(SignUpNavigationService);
  private snackbar = inject(SnackbarService);

  @ViewChild("formfield") formfield!: ElementRef;
  domainFormControl: FormControl = new FormControl("", {
    asyncValidators: [checkDomainValidator(this.createAccountService)],
    validators: [
      Validators.required,
      // regex: alphanumeric & dash, no double dash or start/end dash
      Validators.pattern("^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$"),
      Validators.maxLength(24)
    ],
    updateOn: "submit"
  });

  submitted: "next" | "check" | undefined = undefined;
  private unsubscriber = new Subscription();

  ngOnInit(): void {
    // Retrieve domain if already set and set form; remove spaces as we know they are illegal
    this.entryService.domain &&
      this.domainFormControl.setValue(this.entryService.domain.replace(/\s/g, ""));

    this.unsubscriber.add(
      this.domainFormControl.statusChanges.subscribe(status => {
        if (status === "VALID") {
          if (this.submitted === "next") {
            this.createAccountService.domain = this.domainFormControl.value;
            this.signupNav.navigateForward(Step.SIGNUP_DOMAIN);
          } else if (this.submitted === "check") {
            this.snackbar.openSnackBar(
              this.translate.instant("ONSIP_I18N.DOMAIN_IS_AVAILABLE_FOR_USE"),
              "success"
            );
          }
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.unsubscriber.unsubscribe();
  }

  next(): void {
    if (this.snackbar.snackbarRef) this.snackbar.snackbarRef.dismiss();

    this.domainFormControl.setValue(this.formfield.nativeElement.value);
  }

  showDomainErrors(): string {
    return this.domainFormControl.hasError("pattern")
      ? this.translate.instant(
          "ONSIP_I18N.DOMAIN_MUST_BEGIN_WITH_A_LOWERCASE_LETTER_AND_MAY_CONTAIN_ONLY_LOWERCASE_LETTERS_NUMBERS_AND_SINGLE_NONCONSECUTIVE_DASHES"
        )
      : this.domainFormControl.hasError("maxlength")
        ? this.translate.instant("ONSIP_I18N.CHARACTER_LIMIT_REACHED")
        : this.domainFormControl.hasError("domainUnavailable")
          ? this.translate.instant("ONSIP_I18N.DOMAIN_IS_UNAVAILABLE")
          : this.domainFormControl.hasError("required")
            ? this.translate.instant("ONSIP_I18N.DOMAIN_IS_REQUIRED")
            : "";
  }
}
